import { useState } from "react";
import { api } from "@/lib/api";
import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { FullCheckResult } from "@/components/tool-results";

export function EmailAuthenticationCheckerPage() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runCheck = async () => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await api.tools.checkAll(domain);
      setResult(data);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Free Email Authentication Checker",
    "description": "Check SPF, DKIM, and DMARC records all at once. Complete email authentication validation for your domain. No signup required.",
    "url": "https://getsent.dev/tools/email-authentication-checker",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is email authentication?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Email authentication is a set of protocols (SPF, DKIM, and DMARC) that verify the sender's identity and protect against email spoofing. These DNS-based records help receiving mail servers determine if an email is legitimate."
        }
      },
      {
        "@type": "Question",
        "name": "Why do I need SPF, DKIM, and DMARC?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Each protocol serves a different purpose: SPF verifies sending servers, DKIM verifies message integrity with cryptographic signatures, and DMARC ties them together with policies and reporting. Together, they provide comprehensive protection against email spoofing and phishing."
        }
      },
      {
        "@type": "Question",
        "name": "What happens if my email authentication fails?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Emails that fail authentication may be rejected, sent to spam, or flagged as suspicious depending on the receiving server's policies and your DMARC configuration. This can significantly impact email deliverability."
        }
      }
    ]
  };

  return (
    <div>
      <SEO
        title="Free Email Authentication Checker - SPF, DKIM & DMARC Validator"
        description="Check your email authentication setup instantly. Validate SPF, DKIM, and DMARC records all at once. Free comprehensive email security check - no signup required."
        canonical="/tools/email-authentication-checker"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-indigo-50/50 to-transparent py-16 dark:from-indigo-950/20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-indigo-500/10 px-4 py-1.5 text-sm font-medium text-indigo-600">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
              </svg>
              Free Tool
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Email Authentication{" "}
              <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">
                Checker
              </span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Check your SPF, DKIM, and DMARC records all at once. Complete email
              authentication validation in seconds.
            </p>
          </div>
        </div>
      </section>

      {/* Tool */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="rounded-2xl border bg-card p-6 shadow-lg">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label className="mb-2 block text-sm font-medium">Enter your domain</label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="example.com"
                  className="w-full rounded-lg border bg-background px-4 py-3 text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  onKeyDown={(e) => e.key === "Enter" && runCheck()}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={runCheck}
                  disabled={loading || !domain}
                  className="w-full rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-3 font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30 disabled:opacity-50 sm:w-auto"
                >
                  {loading ? "Checking..." : "Check All"}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-red-600">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-6">
              <FullCheckResult data={result} />
            </div>
          )}
        </div>
      </section>

      {/* What is Email Authentication */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">What is Email Authentication?</h2>
          <div className="mt-4 space-y-4 text-muted-foreground">
            <p>
              <strong className="text-foreground">Email authentication</strong> is a set of protocols
              that verify the sender's identity and protect against email spoofing. The three main
              protocols work together to secure your email:
            </p>
          </div>
          <div className="mt-6 grid gap-4">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-indigo-600">SPF (Sender Policy Framework)</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Specifies which IP addresses and servers are authorized to send email for your domain.
                Receivers check if the sending server is on the authorized list.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-purple-600">DKIM (DomainKeys Identified Mail)</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Adds a cryptographic signature to emails that proves the message hasn't been
                tampered with and was sent by an authorized server.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold text-teal-600">DMARC (Domain-based Message Authentication)</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Ties SPF and DKIM together, specifying what to do when emails fail authentication
                and providing a way to receive reports about authentication results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Why Email Authentication Matters</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border bg-card p-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
                <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="mt-3 font-semibold">Better Deliverability</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Properly authenticated emails are more likely to reach the inbox instead of spam.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
                <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <h3 className="mt-3 font-semibold">Spoofing Protection</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Prevent attackers from sending phishing emails that appear to come from your domain.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
                <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
                </svg>
              </div>
              <h3 className="mt-3 font-semibold">Brand Protection</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Protect your brand reputation by ensuring only authorized emails use your domain.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10">
                <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                </svg>
              </div>
              <h3 className="mt-3 font-semibold">Compliance</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Meet Google and Yahoo requirements for bulk senders (mandatory since 2024).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
          <div className="mt-6 space-y-6">
            <div>
              <h3 className="font-semibold">What is email authentication?</h3>
              <p className="mt-2 text-muted-foreground">
                Email authentication is a set of protocols (SPF, DKIM, and DMARC) that verify
                the sender's identity and protect against email spoofing. These DNS-based records
                help receiving mail servers determine if an email is legitimate.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">Why do I need SPF, DKIM, and DMARC?</h3>
              <p className="mt-2 text-muted-foreground">
                Each protocol serves a different purpose: SPF verifies sending servers, DKIM
                verifies message integrity with cryptographic signatures, and DMARC ties them
                together with policies and reporting. Together, they provide comprehensive
                protection against email spoofing and phishing.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">What happens if my email authentication fails?</h3>
              <p className="mt-2 text-muted-foreground">
                Emails that fail authentication may be rejected, sent to spam, or flagged as
                suspicious depending on the receiving server's policies and your DMARC configuration.
                This can significantly impact email deliverability.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">How long does it take to set up email authentication?</h3>
              <p className="mt-2 text-muted-foreground">
                Setting up SPF, DKIM, and DMARC typically takes 30-60 minutes for simple setups.
                However, you should monitor with <code>p=none</code> for 2-4 weeks before moving
                to enforcement to ensure all legitimate sources are authenticated.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Individual Tools */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center">Individual Tools</h2>
          <p className="mt-2 text-center text-muted-foreground">
            Need to check a specific protocol? Use our individual tools for detailed analysis.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <Link
              to="/tools/spf-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-indigo-600">SPF Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Validate SPF records and check DNS lookup limits.
              </p>
            </Link>
            <Link
              to="/tools/dkim-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-purple-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-purple-600">DKIM Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Validate DKIM and discover selectors.
              </p>
            </Link>
            <Link
              to="/tools/dmarc-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-teal-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-teal-600">DMARC Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Validate DMARC policy configuration.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t py-16">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-8">
          <h2 className="text-2xl font-bold sm:text-3xl">
            Want continuous email authentication monitoring?
          </h2>
          <p className="mt-2 text-muted-foreground">
            Get automated DMARC report analysis and alerts when your authentication breaks. Free for one domain.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-3 font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30"
          >
            Start free monitoring
          </Link>
        </div>
      </section>
    </div>
  );
}
